import { Box, Card, FlexBox, H2, Li, Typography, Ul, tokensRaw } from '@vp/swan';
import { Currency, price } from '@99designs/design-services-common';
import { __ } from '@99designs/i18n';
import { NormalisedBriefProduct } from '../BriefContext';
import StartingProjectDisplay from '../StartingProjectDisplay/StartingProjectDisplay';
import { BriefWorkEntityField } from './FormFields/WorkEntityField';
import { BriefProductFragment, Field_WorkEntityField_Fragment } from './brief.generated';

export interface Product {
    title: BriefProductFragment['title'];
    details: Array<{
        __typename: 'ProductPackageDetails';
        title: string;
        description?: string;
    }>;
    additionalDetails?: Array<{
        __typename: 'ProductPackageDetails';
        title: string;
        description: string;
    }>;
    pricing: BriefProductFragment['pricing'];
    fulfilmentMpvId?: BriefProductFragment['fulfilmentMpvId'];
    description?: string;
}

export interface WorkEntityDisplay {
    title: string;
    description: string | null;
    field: Field_WorkEntityField_Fragment;
}

export interface LogomakerAsset {
    id: string | null | undefined;
    sig: string | null | undefined;
}

export interface StepProductCardProps {
    product: BriefProductFragment | NormalisedBriefProduct;
    startingAsset?: WorkEntityDisplay | LogomakerAsset;
}

export function StepProductCard({ product, startingAsset }: StepProductCardProps) {
    const isStartingAssetWorkEntity = startingAsset && 'field' in startingAsset;
    const isStartingAssetLogomker = startingAsset && 'id' in startingAsset;

    function StartingAssetDisplay() {
        if (isStartingAssetWorkEntity)
            return (
                <Box mt={4}>
                    <BriefWorkEntityField
                        workEntityValue={startingAsset.field.workEntityValue}
                        title={startingAsset.title}
                        description={startingAsset.description}
                    />
                </Box>
            );
        if (isStartingAssetLogomker)
            return (
                <Box mt={4}>
                    <StartingProjectDisplay
                        images={[
                            {
                                url: `https://api.sherbert.cimpress.io/v2/assets/${startingAsset.id}?signature=${startingAsset.sig}`,
                            },
                        ]}
                        title={__('Your Starting Project', {
                            _context:
                                'Title for Logomaker starting project Image, which is used as a reference for designers',
                        })}
                        description={__(
                            'Your designer will use your Logomaker project as a starting point.',
                            {
                                _context:
                                    'Description for Logomaker starting project image, which is used as a reference for designers',
                            }
                        )}
                    />
                </Box>
            );
        return null;
    }

    return (
        <Card
            bordered
            p="5"
            style={{
                minHeight: '96px',
                borderRadius: `${tokensRaw.SwanSemBorderRadiusStandard}`,
                width: '100%',
            }}
        >
            <FlexBox alignItems="center" justifyContent="space-between" flexWrap="wrap-reverse">
                <Box style={{ width: '100%' }}>
                    <FlexBox flexDirection="row" justifyContent="space-between">
                        <H2 fontSize="large" fontWeight="bold" textAlign="left" pr={8} mb={0}>
                            {product.title}
                        </H2>
                        <StepProductPrice
                            amountInCents={product.pricing.amount.amountInCents}
                            currency={product.pricing.amount.currency}
                        />
                    </FlexBox>
                    <Ul mt={'4'}>
                        {product.details.map((item) => (
                            <Li key={item.title}>{item.title}</Li>
                        ))}
                    </Ul>
                    <StartingAssetDisplay />
                </Box>
            </FlexBox>
        </Card>
    );
}

function StepProductPrice({
    amountInCents,
    currency,
}: {
    amountInCents: number;
    currency: string;
}) {
    return (
        <Typography fontSize="large" fontWeight="bold" mb={0}>
            {price({
                amount: amountInCents,
                currency: currency as Currency,
                decimalPlaces: 2,
            })}
        </Typography>
    );
}
